/**
 * Finding library items that a newly imported file duplicates, and asking what to do about each.
 * Pure (the question is an injected async function), so it is unit-tested without a DOM.
 */
import type { MediaAsset, Project } from '@ve/core';

/** What to do with a file that is already in the library. */
export type DuplicateAction = 'replace' | 'keep' | 'add';

export interface DuplicateQuestion {
  fileName: string;
  size: number | undefined;
  existing: MediaAsset;
  /** Same name and same byte size: almost certainly the very same file. */
  identical: boolean;
  /** Duplicates still to be decided after this one (offer "do this for all" when > 0). */
  remaining: number;
}

export interface DuplicateAnswer {
  action: DuplicateAction;
  applyToAll: boolean;
}

export type ImportDecision<T> =
  | { item: T; action: 'add' }
  | { item: T; action: 'replace'; existing: MediaAsset }
  | { item: T; action: 'keep'; existing: MediaAsset };

const fileNameOf = (ref: string) => ref.slice(ref.lastIndexOf('/') + 1);

/**
 * The library item the file named `fileName` duplicates, matched by file name (case-insensitive;
 * the original name or the stored file's name). Prefers an item with the same size.
 */
export function findDuplicate(
  project: Project,
  fileName: string,
  size: number | undefined,
): MediaAsset | undefined {
  const name = fileName.toLowerCase();
  const matches = project.mediaOrder
    .map((id) => project.media[id])
    .filter(
      (a): a is MediaAsset =>
        !!a &&
        (a.origin.fileName.toLowerCase() === name || fileNameOf(a.location.ref).toLowerCase() === name),
    );
  return matches.find((a) => size !== undefined && a.location.size === size) ?? matches[0];
}

/**
 * Decides, for each item to import, whether to add it, or — when it is already in the library —
 * what the user wants (`ask` is called per duplicate until they choose "apply to all").
 */
export async function decideDuplicates<T>(
  project: Project,
  items: Array<{ item: T; fileName: string; size: number | undefined }>,
  ask: (question: DuplicateQuestion) => Promise<DuplicateAnswer>,
): Promise<Array<ImportDecision<T>>> {
  const found = items.map((i) => findDuplicate(project, i.fileName, i.size));
  let remaining = found.filter(Boolean).length;
  let forAll: DuplicateAction | undefined;
  const decisions: Array<ImportDecision<T>> = [];
  for (const [index, { item, fileName, size }] of items.entries()) {
    const existing = found[index];
    if (!existing) {
      decisions.push({ item, action: 'add' });
      continue;
    }
    remaining--;
    let action = forAll;
    if (!action) {
      const answer = await ask({
        fileName,
        size,
        existing,
        identical: size !== undefined && existing.location.size === size,
        remaining,
      });
      action = answer.action;
      if (answer.applyToAll) forAll = action;
    }
    decisions.push(action === 'add' ? { item, action } : { item, action, existing });
  }
  return decisions;
}
